import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ArrowLeft, CalendarDays, Copy, Info, Loader2, RefreshCw } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { generateICalToken, getICalToken, revokeICalToken } from '@/lib/api'
import { BUTTON_STYLES } from '@/lib/buttonStyles'

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-[#6a7282]">
      {children}
    </p>
  )
}

function DarkCard({
  children,
  className = '',
}: {
  children: React.ReactNode
  className?: string
}) {
  return (
    <div
      className={`rounded-2xl border border-[#1e2939] bg-[#131824] shadow-[0_20px_25px_rgba(0,0,0,0.1),0_8px_10px_rgba(0,0,0,0.1)] ${className}`}
    >
      {children}
    </div>
  )
}

export default function CalendarSetup() {
  const navigate = useNavigate()
  const [icalFeedUrl, setICalFeedUrl] = useState<string | null>(null)
  const [icalLoading, setICalLoading] = useState(false)
  const [icalGenerating, setICalGenerating] = useState(false)
  const [icalError, setICalError] = useState<string | null>(null)
  const [icalSuccess, setICalSuccess] = useState<string | null>(null)
  const [copiedToClipboard, setCopiedToClipboard] = useState(false)

  useEffect(() => {
    const loadICalToken = async () => {
      setICalLoading(true)
      setICalError(null)

      try {
        const data = await getICalToken()
        setICalFeedUrl(data.feedUrl)
      } catch (error: any) {
        console.error('Error loading iCal token:', error)
        setICalFeedUrl(null)
      } finally {
        setICalLoading(false)
      }
    }

    loadICalToken()
  }, [])

  async function handleGenerateICalToken() {
    setICalGenerating(true)
    setICalError(null)
    setICalSuccess(null)

    try {
      const data = await generateICalToken()
      setICalFeedUrl(data.feedUrl)
      setICalSuccess('Feed URL generated successfully')
    } catch (error: any) {
      console.error('Error generating iCal token:', error)
      setICalError(error.message || 'Failed to generate iCal token')
    } finally {
      setICalGenerating(false)
    }
  }

  async function handleRegenerateICalToken() {
    setICalGenerating(true)
    setICalError(null)
    setICalSuccess(null)

    try {
      // Revoke old token
      await revokeICalToken()
      // Generate new token
      const data = await generateICalToken()
      setICalFeedUrl(data.feedUrl)
      setICalSuccess('Feed URL regenerated successfully')
      setCopiedToClipboard(false)
    } catch (error: any) {
      console.error('Error regenerating iCal token:', error)
      setICalError(error.message || 'Failed to regenerate iCal token')
    } finally {
      setICalGenerating(false)
    }
  }

  function handleCopyFeedUrl() {
    if (icalFeedUrl) {
      navigator.clipboard.writeText(icalFeedUrl)
      setCopiedToClipboard(true)
      setTimeout(() => setCopiedToClipboard(false), 2000)
    }
  }

  return (
    <div className="mx-auto flex w-full max-w-lg flex-col gap-6 pb-10 pt-1">
      <div className="space-y-4">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate('/settings')}
          className="h-auto w-fit p-0 text-[#99a1af] hover:bg-transparent hover:text-white"
        >
          <ArrowLeft className="h-4 w-4 mr-1" />
          Back to Settings
        </Button>

        <div className="space-y-1.5">
          <h1 className="text-[30px] font-bold leading-9 text-white">Calendar setup</h1>
          <p className="text-sm text-[#99a1af] sm:text-base">
            Manage and customize to sync NIL events
          </p>
        </div>
      </div>

      <div className="rounded-[14px] border border-[rgba(43,127,255,0.2)] bg-linear-to-r from-[rgba(43,127,255,0.1)] to-[rgba(0,184,219,0.1)] px-4 py-4">
        <div className="flex items-start gap-3">
          <div className="mt-0.5 rounded-full text-[#8ec5ff]">
            <Info className="h-4 w-4" />
          </div>
          <p className="text-sm leading-5 text-[#d1d5dc]">
            Subscribe to your iCal feed to keep your NIL deadlines in sync with any calendar app.
          </p>
        </div>
      </div>

      <section className="space-y-4">
        <SectionLabel>Subscribe via iCal</SectionLabel>
        <DarkCard className="p-6">
          <div className="mb-4 flex items-start gap-3 rounded-[14px] border border-[rgba(54,65,83,0.45)] bg-[#1a2030] px-4 py-3.5">
            <CalendarDays className="mt-0.5 h-4 w-4 text-[#8ec5ff]" />
            <div>
              <p className="text-sm font-medium text-white">Add to any calendar app</p>
              <p className="mt-1 text-sm leading-5 text-[#99a1af]">
                Generate a shareable calendar feed URL that you can add to Apple Calendar, Google
                Calendar, Outlook, or any iCal-compatible app. The feed automatically refreshes
                with your latest NIL deadlines.
              </p>
            </div>
          </div>

          {icalLoading ? (
            <div className="flex items-center gap-3 rounded-[14px] border border-[rgba(54,65,83,0.45)] bg-[#1a2030] px-4 py-4 text-sm text-[#99a1af]">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading feed status...
            </div>
          ) : !icalFeedUrl ? (
            <div className="space-y-4">
              {icalError ? (
                <div className="rounded-[14px] border border-red-500/25 bg-red-500/10 px-4 py-3 text-sm text-red-300">
                  {icalError}
                </div>
              ) : null}
              <Button
                onClick={handleGenerateICalToken}
                disabled={icalGenerating}
                className={`w-full h-10 rounded-[14px] px-5 text-sm font-semibold ${BUTTON_STYLES.primary}`}
              >
                {icalGenerating ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin inline" />
                    Generating...
                  </>
                ) : (
                  'Generate Feed URL'
                )}
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              {icalError ? (
                <div className="rounded-[14px] border border-red-500/25 bg-red-500/10 px-4 py-3 text-sm text-red-300">
                  {icalError}
                </div>
              ) : null}
              {icalSuccess ? (
                <div className="rounded-[14px] border border-emerald-500/25 bg-emerald-500/10 px-4 py-3 text-sm text-emerald-300">
                  {icalSuccess}
                </div>
              ) : null}

              <div className="space-y-2">
                <label className="block text-sm font-medium text-[#99a1af]">Your feed URL</label>
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    value={icalFeedUrl}
                    readOnly
                    className="flex-1 rounded-[14px] border border-[rgba(54,65,83,0.45)] bg-[#1a2030] px-4 py-3 text-sm text-white font-mono break-all"
                  />
                  <Button
                    size="sm"
                    onClick={handleCopyFeedUrl}
                    className={`h-10 px-3 rounded-[14px] ${BUTTON_STYLES.secondary}`}
                  >
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
                {copiedToClipboard ? (
                  <p className="text-xs text-emerald-400">Copied to clipboard!</p>
                ) : null}
              </div>

              <p className="text-xs text-[#6a7282]">
                Copy this URL and paste it into your calendar app's "Subscribe to Calendar" or "Add
                Calendar by URL" option. Updates automatically.
              </p>

              <Button
                onClick={handleRegenerateICalToken}
                disabled={icalGenerating}
                className={`w-full h-10 rounded-[14px] ${BUTTON_STYLES.outline}`}
              >
                {icalGenerating ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin inline" />
                    Regenerating...
                  </>
                ) : (
                  <>
                    <RefreshCw className="h-4 w-4 mr-2" />
                    Regenerate Feed URL
                  </>
                )}
              </Button>
            </div>
          )}
        </DarkCard>
      </section>
    </div>
  )
}
